import { StyledAsyncSelect } from "./StyledAsyncSelect";
import { useTRPC } from "@/trpc";
import { useQueryClient } from "@tanstack/react-query";
import { useId, useRef, useState } from "react";
import { PROFILE_SELECT_CLASS_NAMES } from "./profile-select-styles";
import type { ClassNamesConfig } from "react-select";
import { SELECT_CLASS_NAMES } from "@/components/Selects/select-class-names";
import { cn } from "@/lib/utils";

const MINIMUM_SEARCH_LENGTH = 3;
const NPI_SEARCH_STALE_MS = 10 * 60 * 1000;

export type ProviderNpiValue = {
  firstName: string;
  lastName: string;
  npi: string;
  specialties: {
    code: string;
    desc?: string | null;
    primary?: boolean | null;
    state?: string | null;
    taxonomy_group?: string | null;
  }[];
  providerTypeId: string | null;
  middleName?: string | null;
  suffixName?: string | null;
};
type ProviderNpiSearchResult = ProviderNpiValue & {
  address?: {
    city?: string | null;
    state?: string | null;
  } | null;
};

function npiOptionLabel(value: ProviderNpiSearchResult): string {
  const name = [value.firstName, value.middleName, value.lastName]
    .filter(Boolean)
    .join(" ");
  const specialties = value.specialties
    .map((specialty) => specialty.desc)
    .filter(Boolean)
    .join(", ");
  const address = [value.address?.city, value.address?.state]
    .filter(Boolean)
    .join(", ");
  return [name, specialties, address, String(value.npi)]
    .filter(Boolean)
    .join(" · ");
}

export function ProviderNpiSelect({
  value,
  onChange,
  placeholder = "Enter your name",
  disabled = false,
  size = "default",
  appearance = "default",
  inputId,
  classNames,
  searchErrorMessage = "Could not search the NPI registry. Try again or enter your details manually.",
}: {
  value?: ProviderNpiValue;
  onChange?: (value: ProviderNpiValue | null) => void;
  placeholder?: string;
  disabled?: boolean;
  size?: "default" | "lg";
  appearance?: "default" | "profile";
  inputId?: string;
  classNames?: ClassNamesConfig<ProviderNpiSearchResult, false>;
  searchErrorMessage?: string;
}) {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const errorId = useId();
  const latestSearch = useRef("");
  const [searchError, setSearchError] = useState<string | null>(null);
  let appearanceClassNames:
    | ClassNamesConfig<ProviderNpiSearchResult, false>
    | undefined;
  if (appearance === "profile") {
    appearanceClassNames = PROFILE_SELECT_CLASS_NAMES;
  } else if (size === "lg") {
    appearanceClassNames = {
      control: (state) => cn(SELECT_CLASS_NAMES.control(state), "!min-h-14"),
    };
  }

  // Function to calculate relevance score for search results
  const calculateRelevanceScore = (
    result: ProviderNpiSearchResult,
    searchTerms: string[],
  ) => {
    let score = 0;
    const searchTermsLower = searchTerms.map((term) => term.toLowerCase());

    // Helper function to check if any search term matches a field
    const getMatchScore = (field: string, weight: number) => {
      const fieldLower = field.toLowerCase();
      let fieldScore = 0;

      searchTermsLower.forEach((term) => {
        if (fieldLower.includes(term)) {
          // Exact match gets higher score
          if (fieldLower === term) {
            fieldScore += weight * 3;
          }
          // Starts with term gets medium score
          else if (fieldLower.startsWith(term)) {
            fieldScore += weight * 2;
          }
          // Contains term gets base score
          else {
            fieldScore += weight;
          }
        }
      });

      return fieldScore;
    };

    // Score different fields with different weights
    score += getMatchScore(result.firstName || "", 10); // High weight for first name
    score += getMatchScore(result.lastName || "", 10); // High weight for last name
    score += getMatchScore(`${result.firstName} ${result.lastName}`, 15); // Highest for full name
    score += getMatchScore(result.npi || "", 5); // Medium weight for NPI

    // Score specialties
    result.specialties.forEach((specialty) => {
      score += getMatchScore(specialty.desc || "", 3); // Lower weight for specialties
    });

    // Score address if available
    if (result.address) {
      score += getMatchScore(result.address.city || "", 2);
      score += getMatchScore(result.address.state || "", 2);
    }

    return score;
  };

  const loadNpi = async (inputValue: string) => {
    latestSearch.current = inputValue;
    setSearchError(null);
    if (inputValue.trim().length < MINIMUM_SEARCH_LENGTH) {
      return [];
    }

    try {
      const results = await queryClient.fetchQuery(
        trpc.npi.search.queryOptions(
          {
            search: inputValue,
          },
          {
            staleTime: NPI_SEARCH_STALE_MS,
            retry: false,
          },
        ),
      );
      // Prepare search terms for scoring
      const searchTerms = inputValue
        .trim()
        .split(/\s+/)
        .filter((term) => term.length > 0);

      // Map and score results
      const scoredResults = results.map((result) => {
        // The registry may return the NPI as a JSON number. Select values and
        // credential identifiers remain strings throughout the form.
        const normalized = { ...result, npi: String(result.npi) };
        const score = calculateRelevanceScore(normalized, searchTerms);
        return {
          ...normalized,
          _score: score,
        };
      });

      // Sort by relevance score (highest first)
      return scoredResults.sort((a, b) => b._score - a._score);
    } catch {
      if (latestSearch.current === inputValue) {
        setSearchError(searchErrorMessage);
      }
      return [];
    }
  };

  return (
    <div className="space-y-2">
      <StyledAsyncSelect<ProviderNpiSearchResult>
        inputId={inputId}
        aria-describedby={searchError ? errorId : undefined}
        value={value}
        getOptionLabel={npiOptionLabel}
        getOptionValue={(option) => String(option.npi)}
        loadOptions={loadNpi}
        onChange={onChange}
        isMulti={false}
        isDisabled={disabled}
        placeholder={placeholder}
        isClearable
        noOptionsMessage={({ inputValue }) =>
          inputValue.length < MINIMUM_SEARCH_LENGTH
            ? "Please enter at least 3 characters to search"
            : "No options found"
        }
        classNames={{ ...appearanceClassNames, ...classNames }}
        menuPlacement="auto"
      />
      {searchError && (
        <p id={errorId} role="alert" className="text-xs text-red-700">
          {searchError}
        </p>
      )}
    </div>
  );
}
